'use client';

import { useEffect, useRef } from 'react';

const reviews = [
  {
    name: 'Jessica M.',
    location: 'Austin, TX',
    rating: 5,
    text: '"I lost 8 lbs in 3 weeks without changing my diet. The only thing I added was NightBurnOJ before bed. I wake up less bloated every single morning."',
    result: '−8 lbs / 3 weeks',
    avatar: '👩',
  },
  {
    name: 'David K.',
    location: 'Chicago, IL',
    rating: 5,
    text: '"Skeptical at first — I\'ve tried everything. But my belly fat actually started shrinking after week 2. My wife noticed before I did."',
    result: '−2.5 inches waist',
    avatar: '👨',
  },
  {
    name: 'Priya S.',
    location: 'San Jose, CA',
    rating: 5,
    text: '"I love that it\'s just orange juice — no weird powders or pills. Tastes amazing, and I can see the difference in my stomach after 30 days."',
    result: '−6 lbs / 4 weeks',
    avatar: '👩🏽',
  },
  {
    name: 'Marcus T.',
    location: 'Atlanta, GA',
    rating: 5,
    text: '"My trainer was shocked at my progress. I told him all I changed was drinking this before bed. He\'s now recommending it to all his clients."',
    result: '−11 lbs / 6 weeks',
    avatar: '👨🏾',
  },
  {
    name: 'Lauren B.',
    location: 'Denver, CO',
    rating: 5,
    text: '"I sleep so much better now too. The magnesium in it is real — I fall asleep faster and wake up genuinely rested. The fat loss is just a bonus."',
    result: '−5 lbs / 3 weeks',
    avatar: '👩🏼',
  },
  {
    name: 'Carlos R.',
    location: 'Miami, FL',
    rating: 5,
    text: '"I was waking up at 3am every night. Now I sleep through. AND I\'ve dropped 14 lbs. NightBurnOJ fixed two problems I didn\'t think were related."',
    result: '−14 lbs / 8 weeks',
    avatar: '👨🏻',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#FF6B1A">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: typeof reviews[0] }) {
  return (
    <div className="glass-card rounded-2xl p-6 w-80 shrink-0 mx-3 hover:border-citrus/25 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-citrus/10 flex items-center justify-center text-xl">{review.avatar}</div>
          <div>
            <p className="text-cream font-semibold text-sm">{review.name}</p>
            <p className="text-muted text-xs">{review.location}</p>
          </div>
        </div>
        <div
          className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(255,107,26,0.12)', color: '#FF6B1A' }}
        >
          {review.result}
        </div>
      </div>
      <StarRating count={review.rating} />
      <p className="text-cream-muted text-sm leading-relaxed mt-3 font-light">{review.text}</p>
    </div>
  );
}

export default function ReviewsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Duplicate for seamless loop
  const doubled = [...reviews, ...reviews];

  return (
    <section
      id="reviews"
      ref={sectionRef}
      className="py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0D0F1A 0%, #0F1115 50%, #0D0F1A 100%)' }}
    >
      {/* bg */}
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] rounded-full blur-[140px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,107,26,0.05) 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 mb-16">
        <div className="text-center reveal">
          <p className="text-citrus font-bold uppercase tracking-[0.3em] text-xs mb-4">Real Results</p>
          <h2 className="font-fraunces text-5xl md:text-7xl font-bold tracking-tight leading-[0.9] mb-4">
            847 people
            <br />
            <span className="gradient-text-citrus italic">woke up leaner.</span>
          </h2>
          <p className="text-cream-muted text-lg max-w-lg mx-auto">
            Average result: <span className="text-amber font-semibold">−7.2 lbs in 30 days</span>. No diet changes required.
          </p>
        </div>
      </div>

      {/* Row 1 — scrolls left */}
      <div className="overflow-hidden mb-4">
        <div className="marquee-left flex">
          {doubled.map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </div>
      </div>

      {/* Row 2 — scrolls right */}
      <div className="overflow-hidden">
        <div className="marquee-right flex">
          {[...doubled].reverse().map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </div>
      </div>

      {/* Summary bar */}
      <div className="max-w-4xl mx-auto px-6 mt-16">
        <div className="glass-card rounded-2xl p-6 reveal">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { val: '4.9/5', label: 'Average rating', sub: 'From 847 reviews' },
              { val: '94%', label: 'Saw results', sub: 'Within 30 days' },
              { val: '−7.2 lbs', label: 'Average loss', sub: 'In first month' },
              { val: '98%', label: 'Would reorder', sub: 'Subscription rate' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-fraunces font-black gradient-text-citrus">{s.val}</p>
                <p className="text-cream text-sm font-semibold mt-1">{s.label}</p>
                <p className="text-muted text-xs mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}